import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { FileText, Download, Eye, CheckCircle, XCircle, AlertTriangle, Calendar, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegulatoryReports = ({ soundmeters }) => {
  const [selectedSoundmeter, setSelectedSoundmeter] = useState('');
  const [regulationType, setRegulationType] = useState('erp_music');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [childrenPresent, setChildrenPresent] = useState(false);
  
  // Report content options
  const [includeExecutiveSummary, setIncludeExecutiveSummary] = useState(true);
  const [includeComplianceTable, setIncludeComplianceTable] = useState(true);
  const [includeTemporalGraph, setIncludeTemporalGraph] = useState(true);
  const [includeSpectrumGraph, setIncludeSpectrumGraph] = useState(false);
  const [includeHistogram, setIncludeHistogram] = useState(true);
  const [includeMethodology, setIncludeMethodology] = useState(true);
  const [includeConclusions, setIncludeConclusions] = useState(true);
  
  // Site/Company info
  const [siteName, setSiteName] = useState('');
  const [siteAddress, setSiteAddress] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [reportAuthor, setReportAuthor] = useState('');
  
  // State
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [savedReports, setSavedReports] = useState([]);

  useEffect(() => {
    if (soundmeters && soundmeters.length > 0 && !selectedSoundmeter) {
      setSelectedSoundmeter(soundmeters[0].soundmeter_id);
    }
    
    // Set default dates (last 7 days)
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    
    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
    
    loadSavedReports();
  }, [soundmeters]);

  const loadSavedReports = async () => {
    try {
      const response = await axios.get(`${API}/reports/regulatory/list`);
      setSavedReports(response.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const previewCompliance = async () => {
    if (!selectedSoundmeter || !startDate || !endDate) {
      toast.error("Veuillez sélectionner un sonomètre et une période");
      return;
    }

    setPreviewing(true);
    try {
      const response = await axios.get(`${API}/reports/regulatory/preview-compliance`, {
        params: {
          soundmeter_id: selectedSoundmeter,
          start_date: new Date(startDate).toISOString(),
          end_date: new Date(endDate).toISOString(),
          regulation_type: regulationType,
          children_present: childrenPresent
        }
      });

      setPreviewData(response.data);
      toast.success("Aperçu généré avec succès");
    } catch (error) {
      toast.error("Erreur lors de l'aperçu");
      console.error(error);
    } finally {
      setPreviewing(false);
    }
  };

  const generateReport = async () => {
    if (!selectedSoundmeter || !startDate || !endDate) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setGenerating(true);
    try {
      const config = {
        soundmeter_ids: [selectedSoundmeter],
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        regulation_type: regulationType,
        children_present: childrenPresent,
        include_executive_summary: includeExecutiveSummary,
        include_compliance_table: includeComplianceTable,
        include_temporal_graph: includeTemporalGraph,
        include_spectrum_graph: includeSpectrumGraph,
        include_histogram: includeHistogram,
        include_methodology: includeMethodology,
        include_conclusions: includeConclusions,
        site_name: siteName || null,
        site_address: siteAddress || null,
        company_name: companyName || null,
        report_author: reportAuthor || null
      };

      const response = await axios.post(`${API}/reports/regulatory/generate`, config, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapport_reglementaire_${new Date().getTime()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Rapport généré et téléchargé avec succès !");
      loadSavedReports();
      setPreviewData(null);
    } catch (error) {
      toast.error("Erreur lors de la génération du rapport");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  const getComplianceColor = (isCompliant) => {
    return isCompliant ? 'text-green-600' : 'text-red-600';
  };

  const getComplianceIcon = (isCompliant) => {
    return isCompliant ? (
      <CheckCircle className="h-5 w-5 text-green-600" />
    ) : (
      <XCircle className="h-5 w-5 text-red-600" />
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>Génération de Rapports Réglementaires</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Configuration Section */}
          <div className="space-y-4">
            <h3 className="font-medium text-lg">Configuration du Rapport</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Soundmeter Selection */}
              <div>
                <Label>Sonomètre *</Label>
                <Select value={selectedSoundmeter} onValueChange={setSelectedSoundmeter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un sonomètre" />
                  </SelectTrigger>
                  <SelectContent>
                    {soundmeters?.map(sm => (
                      <SelectItem key={sm.soundmeter_id} value={sm.soundmeter_id}>
                        {sm.name || sm.soundmeter_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Regulation Type */}
              <div>
                <Label>Type de réglementation *</Label>
                <Select value={regulationType} onValueChange={setRegulationType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="erp_music">🎵 ERP Musique Amplifiée</SelectItem>
                    <SelectItem value="neighborhood">🏘️ Nuisance de voisinage</SelectItem>
                    <SelectItem value="icpe">🏭 ICPE</SelectItem>
                    <SelectItem value="construction">🚧 Chantiers</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div>
                <Label>Date de début *</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>

              {/* End Date */}
              <div>
                <Label>Date de fin *</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>

            {/* ERP Music specific options */}
            {regulationType === 'erp_music' && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="children-present"
                    checked={childrenPresent}
                    onCheckedChange={setChildrenPresent}
                  />
                  <div className="flex-1">
                    <Label htmlFor="children-present" className="cursor-pointer">
                      Présence d'enfants de moins de 6 ans
                    </Label>
                    <p className="text-sm text-slate-600 mt-1">
                      Limite réglementaire : {childrenPresent ? '94 dB(A)' : '102 dB(A)'} / 118 dB(C)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Site/Company Information */}
          <div className="space-y-4">
            <h3 className="font-medium text-lg flex items-center space-x-2">
              <Building2 className="h-5 w-5" />
              <span>Informations du Site (Optionnel)</span>
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Nom de l'établissement</Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Ex: Bar Le Groove"
                />
              </div>

              <div>
                <Label>Nom du site</Label>
                <Input
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="Ex: Salle principale"
                />
              </div>

              <div>
                <Label>Adresse</Label>
                <Input
                  value={siteAddress}
                  onChange={(e) => setSiteAddress(e.target.value)}
                  placeholder="Ex: 123 Rue de la Musique, 75001 Paris"
                />
              </div>

              <div>
                <Label>Rédacteur du rapport</Label>
                <Input
                  value={reportAuthor}
                  onChange={(e) => setReportAuthor(e.target.value)}
                  placeholder="Ex: Jean Dupont, Acousticien"
                />
              </div>
            </div>
          </div>

          {/* Report Content Selection */}
          <div className="space-y-4">
            <h3 className="font-medium text-lg">Contenu du Rapport</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="exec-summary"
                  checked={includeExecutiveSummary}
                  onCheckedChange={setIncludeExecutiveSummary}
                />
                <Label htmlFor="exec-summary" className="cursor-pointer">Résumé exécutif</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="compliance-table"
                  checked={includeComplianceTable}
                  onCheckedChange={setIncludeComplianceTable}
                />
                <Label htmlFor="compliance-table" className="cursor-pointer">Tableau de conformité</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="temporal-graph"
                  checked={includeTemporalGraph}
                  onCheckedChange={setIncludeTemporalGraph}
                />
                <Label htmlFor="temporal-graph" className="cursor-pointer">Graphique temporel</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="spectrum-graph"
                  checked={includeSpectrumGraph}
                  onCheckedChange={setIncludeSpectrumGraph}
                />
                <Label htmlFor="spectrum-graph" className="cursor-pointer">Analyse spectrale</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="histogram"
                  checked={includeHistogram}
                  onCheckedChange={setIncludeHistogram}
                />
                <Label htmlFor="histogram" className="cursor-pointer">Histogramme</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="methodology"
                  checked={includeMethodology}
                  onCheckedChange={setIncludeMethodology}
                />
                <Label htmlFor="methodology" className="cursor-pointer">Méthodologie</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="conclusions"
                  checked={includeConclusions}
                  onCheckedChange={setIncludeConclusions}
                />
                <Label htmlFor="conclusions" className="cursor-pointer">Conclusions</Label>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              onClick={previewCompliance}
              disabled={previewing || !selectedSoundmeter}
              variant="outline"
              className="flex-1"
            >
              {previewing ? (
                <>
                  <AlertTriangle className="h-4 w-4 mr-2 animate-spin" />
                  Analyse en cours...
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  Aperçu Conformité
                </>
              )}
            </Button>

            <Button
              onClick={generateReport}
              disabled={generating || !selectedSoundmeter}
              className="flex-1 bg-teal-600 hover:bg-teal-700"
            >
              {generating ? (
                <>
                  <Download className="h-4 w-4 mr-2 animate-spin" />
                  Génération...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Générer le Rapport PDF
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Panel */}
      {previewData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Aperçu de Conformité</span>
              {getComplianceIcon(previewData.compliance.is_compliant)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className={previewData.compliance.is_compliant ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}>
              <AlertDescription>
                <div className={`font-bold text-lg ${getComplianceColor(previewData.compliance.is_compliant)}`}>
                  {previewData.compliance.is_compliant ? '✓ CONFORME' : '✗ NON CONFORME'}
                </div>
                <div className="mt-2 text-sm">
                  {previewData.compliance.regulation_type}
                </div>
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-slate-50 rounded">
                <div className="text-sm text-slate-600">Limite dB(A)</div>
                <div className="text-xl font-bold">{previewData.compliance.limit_dba} dB</div>
              </div>

              <div className="p-3 bg-slate-50 rounded">
                <div className="text-sm text-slate-600">Max mesuré dB(A)</div>
                <div className={`text-xl font-bold ${previewData.compliance.max_measured_dba > previewData.compliance.limit_dba ? 'text-red-600' : 'text-green-600'}`}>
                  {previewData.compliance.max_measured_dba} dB
                </div>
              </div>

              <div className="p-3 bg-slate-50 rounded">
                <div className="text-sm text-slate-600">Limite dB(C)</div>
                <div className="text-xl font-bold">{previewData.compliance.limit_dbc} dB</div>
              </div>

              <div className="p-3 bg-slate-50 rounded">
                <div className="text-sm text-slate-600">Max mesuré dB(C)</div>
                <div className={`text-xl font-bold ${previewData.compliance.max_measured_dbc > previewData.compliance.limit_dbc ? 'text-red-600' : 'text-green-600'}`}>
                  {previewData.compliance.max_measured_dbc} dB
                </div>
              </div>
            </div>

            {previewData.compliance.exceedances_count > 0 && (
              <div className="p-4 bg-red-50 rounded border border-red-200">
                <div className="font-medium text-red-800">
                  {previewData.compliance.exceedances_count} dépassement(s) détecté(s)
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  Les détails complets seront dans le rapport PDF
                </div>
              </div>
            )}

            <div className="text-sm text-slate-600">
              Points de mesure analysés: {previewData.total_points}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Saved Reports History */}
      {savedReports.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Calendar className="h-5 w-5" />
              <span>Rapports Générés</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {savedReports.slice(0, 10).map((report) => (
                <div
                  key={report.report_id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100"
                >
                  <div className="flex-1">
                    <div className="font-medium">
                      Rapport {report.report_id.substring(0, 8)}
                    </div>
                    <div className="text-sm text-slate-600">
                      {new Date(report.created_at).toLocaleDateString('fr-FR')} - {report.regulation_type}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Badge
                      variant={report.compliance_status ? 'default' : 'destructive'}
                      className={report.compliance_status ? 'bg-green-500' : 'bg-red-500'}
                    >
                      {report.compliance_status ? 'Conforme' : 'Non conforme'}
                    </Badge>
                    {report.exceedances_count > 0 && (
                      <span className="text-sm text-red-600">
                        {report.exceedances_count} dépassements
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RegulatoryReports;
